'use client';

/**
 * 更新日志弹窗组件
 * 用户登录时显示未读的系统更新日志
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Sparkles, Bug, Zap, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

// =====================================================
// Types
// =====================================================

export interface Changelog {
  id: number;
  version: string | null;
  title: string;
  content: string;
  type: 'feature' | 'bugfix' | 'improvement' | 'update';
  published_at: string;
  created_at: string;
}

interface ChangelogModalProps {
  show: boolean;
  changelogs: Changelog[];
  onClose: () => void;
  onMarkRead: (ids: number[]) => Promise<void>;
}

// =====================================================
// Config
// =====================================================

const typeConfig: Record<string, { icon: typeof Sparkles; label: string; color: string }> = {
  feature: { icon: Sparkles, label: '新功能', color: 'text-green-600 bg-green-50' },
  bugfix: { icon: Bug, label: '修复', color: 'text-red-600 bg-red-50' },
  improvement: { icon: Zap, label: '优化', color: 'text-blue-600 bg-blue-50' },
  update: { icon: RefreshCw, label: '更新', color: 'text-purple-600 bg-purple-50' },
};

// =====================================================
// Component
// =====================================================

export default function ChangelogModal({
  show,
  changelogs,
  onClose,
  onMarkRead,
}: ChangelogModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [marking, setMarking] = useState(false);

  // Reset index when modal opens
  useEffect(() => {
    if (show) {
      setCurrentIndex(0);
    }
  }, [show]);

  const handleClose = useCallback(async () => {
    if (changelogs.length === 0) {
      onClose();
      return;
    }

    setMarking(true);
    try {
      // Mark all displayed changelogs as read
      const ids = changelogs.map(c => c.id);
      await onMarkRead(ids);
    } catch (error) {
      console.error('Failed to mark changelogs as read:', error);
    } finally {
      setMarking(false);
      onClose();
    }
  }, [changelogs, onMarkRead, onClose]);

  if (!show || changelogs.length === 0) return null;

  const current = changelogs[currentIndex];
  const config = typeConfig[current.type] || typeConfig.update;
  const Icon = config.icon;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < changelogs.length - 1;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">系统更新</h2>
              <p className="text-sm text-gray-500">
                {changelogs.length > 1
                  ? `${currentIndex + 1} / ${changelogs.length} 条更新`
                  : '有新的更新内容'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={marking}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {/* Type Badge & Version */}
          <div className="flex items-center gap-2 mb-3">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
              <Icon className="w-3.5 h-3.5" />
              {config.label}
            </span>
            {current.version && (
              <span className="text-xs text-gray-400 font-mono">
                {current.version}
              </span>
            )}
            <span className="text-xs text-gray-400">
              {formatDate(current.published_at || current.created_at)}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            {current.title}
          </h3>

          {/* Content (Markdown) */}
          <div className="prose prose-sm prose-gray max-w-none">
            <ReactMarkdown
              components={{
                // Custom styling for markdown elements
                h1: ({ children }) => <h1 className="text-lg font-bold mt-4 mb-2">{children}</h1>,
                h2: ({ children }) => <h2 className="text-base font-semibold mt-3 mb-2">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sm font-medium mt-2 mb-1">{children}</h3>,
                ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>,
                li: ({ children }) => <li className="text-gray-700">{children}</li>,
                p: ({ children }) => <p className="text-gray-700 mb-2">{children}</p>,
                code: ({ children }) => (
                  <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-pink-600">
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-3">
                    {children}
                  </pre>
                ),
              }}
            >
              {current.content}
            </ReactMarkdown>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentIndex(i => i - 1)}
              disabled={!hasPrev}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentIndex(i => i + 1)}
              disabled={!hasNext}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Close Button */}
          <button
            onClick={handleClose}
            disabled={marking}
            className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium text-sm hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {marking ? '处理中...' : '我知道了'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// Hook: useChangelogCheck
// =====================================================

export function useChangelogCheck() {
  const [changelogs, setChangelogs] = useState<Changelog[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkUnread = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/changelog/unread');
      const data = await response.json();

      if (data.success && data.changelogs && data.changelogs.length > 0) {
        setChangelogs(data.changelogs);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Failed to check changelog:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const markRead = useCallback(async (ids: number[]) => {
    try {
      await fetch('/api/admin/changelog/read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changelog_ids: ids }),
      });
    } catch (error) {
      console.error('Failed to mark changelog as read:', error);
    }
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setChangelogs([]);
  }, []);

  return {
    changelogs,
    showModal,
    loading,
    checkUnread,
    markRead,
    closeModal,
  };
}
